import React, { FunctionComponent } from 'react';
import { OptionsType, OptionTypeBase } from 'react-select';
import { Formik, FormikHelpers, Field, FieldProps } from 'formik';
import { object, string } from 'yup';
import reduce from 'lodash/reduce';
import includes from 'lodash/includes';
import { Button, Grid } from '@inplayer-org/inplayer-ui';

// Types
import { Follower } from 'ducks/merchantDucks/agency/types';
import { PaymentMethod } from 'ducks/merchantDucks/paymentMethods/types';
import { Scopes } from 'pages/AdminPages/AdminMerchants/PaymentManagement/components/PaymentSetup';

// actions
import { UpdateFollowerPayload } from 'ducks/merchantDucks/agency/actions';

// Components
import Input from 'components/Fields/Input';
import FieldError from 'components/Fields/FieldError';
import StyledContainer from 'components/StyledContainer';
import PaymentInheritance from './PaymentInheritance';

const { Container, Cell } = Grid;

export interface OwnProps {
  isFetching: boolean;
  onSubmit: (data: UpdateFollowerPayload) => any;
  scopeOptions: OptionsType<OptionTypeBase>;
  handleCloseModal: () => any;
  follower: Partial<Follower>;
  paymentMethods: Array<PaymentMethod>;
  followerMethods: Array<PaymentMethod>;
  isMethodsFetching: boolean;
  editMode: boolean;
  noMethods: boolean;
  addMerchantMode?: boolean;
  merchantId?: number;
  merchantEmail?: string;
}

type Props = OwnProps;

const formSchema = {
  email: string().email().required('*Required'),
  full_name: string().required('*Required'),
};

const CreateUpdateClientAgentForm: FunctionComponent<Props> = ({
  onSubmit,
  scopeOptions,
  handleCloseModal,
  follower,
  paymentMethods,
  followerMethods,
  isMethodsFetching,
  editMode,
  noMethods,
  addMerchantMode = false,
  merchantId = 0,
  merchantEmail = '',
}) => {
  const {
    email,
    full_name, // Api call for create/update follower accepts full_name as body parameter
    password,
  } = follower;

  const formSchemaWithPassword = {
    ...formSchema,
    password: string().required('*Required').min(8, 'Password must be minimum 8 characters'),
  };

  let validationSchema = object().shape({});

  if (!addMerchantMode) {
    validationSchema = object().shape(editMode ? formSchema : formSchemaWithPassword);
  }

  const createFormMethods = reduce(
    paymentMethods,
    (mappedMethods, method) => ({ ...mappedMethods, [method.payment_method.id]: method.active }),
    {}
  );
  const editFormMethods =
    editMode && includes(follower.scopes, Scopes.INHERITANCE) && followerMethods.length > 0
      ? reduce(followerMethods, (mappedMethods, method) => ({ ...mappedMethods, [method.id]: method.active }), {})
      : reduce(paymentMethods, (mappedMethods, method) => ({ ...mappedMethods, [method.id]: method.active }), {});

  const createFollowerInitialValues = {
    email: '',
    full_name: '',
    password: '',
    scopes: [],
    methods: createFormMethods,
  };

  const editModeInitialValues = {
    email: email || '',
    full_name: full_name || '',
    password: password || '',
    scopes: follower.scopes || [],
    methods: editFormMethods,
  };

  let initialValues: Record<string, any> = { email: merchantEmail, follower_id: merchantId };

  if (!addMerchantMode) {
    initialValues = editMode ? editModeInitialValues : createFollowerInitialValues;
  }

  const onFormSubmit = async (
    values: UpdateFollowerPayload,
    { setSubmitting, resetForm }: FormikHelpers<UpdateFollowerPayload>
  ) => {
    try {
      await onSubmit(values);
      handleCloseModal();
      resetForm();
      setSubmitting(false);
    } catch (e) {
      resetForm();
      setSubmitting(false);
    }
  };

  const handleClose = (resetForm: () => void) => () => {
    resetForm();
    handleCloseModal();
  };

  return (
    <Formik<UpdateFollowerPayload>
      enableReinitialize
      onSubmit={onFormSubmit}
      validationSchema={validationSchema}
      initialValues={initialValues}
    >
      {({ handleSubmit, isSubmitting, values, resetForm }) => (
        <form onSubmit={handleSubmit}>
          <Container alignContent="space-around" columns={1}>
            {addMerchantMode && (
              <Cell>
                <Field
                  name="follower_id"
                  type="number"
                  component={Input}
                  label="ID"
                  disabled={addMerchantMode}
                  value={addMerchantMode && initialValues.follower_id}
                />
                <FieldError name="email" />
              </Cell>
            )}
            <Cell>
              <Field
                tag="text_email"
                name="email"
                type="email"
                component={Input}
                label="Email"
                disabled={addMerchantMode || editMode}
              />
              <FieldError name="email" />
            </Cell>
            {!addMerchantMode && (
              <Cell>
                <Field
                  tag="text_full_name"
                  name="full_name"
                  type="text"
                  component={Input}
                  label="Full name"
                  disabled={addMerchantMode || editMode}
                />
                <FieldError name="full_name" />
              </Cell>
            )}
            {addMerchantMode ||
              (!editMode && (
                <Cell>
                  <Field tag="text_password" name="password" type="password" component={Input} label="Password" />
                  <FieldError name="password" />
                </Cell>
              ))}
          </Container>
          <Field name="scopes">
            {({ form: { setFieldValue } }: FieldProps<UpdateFollowerPayload>) => {
              const onChange = (scopes: Array<string>, methods: any) => {
                setFieldValue('scopes', scopes);
                // Set default merchant's or follower's payment methods if
                // inheritance scope is selected after it's been deleted
                const defaultMethods =
                  editMode && followerMethods.length > 0
                    ? reduce(
                        followerMethods,
                        (mappedMethods, method) => ({ ...mappedMethods, [method.id]: method.active }),
                        {}
                      )
                    : reduce(
                        paymentMethods,
                        (mappedMethods, method) => ({ ...mappedMethods, [method.id]: method.active }),
                        {}
                      );
                const selectedMethods = methods || defaultMethods;
                setFieldValue('methods', selectedMethods);
              };

              const handleCheckboxCheck = (id: number, checked: boolean) => () => {
                setFieldValue('methods', { ...values.methods, [id]: checked });
              };

              return (
                <PaymentInheritance
                  scopeOptions={scopeOptions}
                  selectedScopes={values.scopes || []}
                  follower={follower}
                  onChange={onChange}
                  paymentMethods={paymentMethods}
                  selectedMethods={values.methods || []}
                  isMethodsFetching={isMethodsFetching}
                  handleCheckboxCheck={handleCheckboxCheck}
                  editMode={editMode}
                  noMethods={noMethods}
                  isFetching={isSubmitting}
                />
              );
            }}
          </Field>
          <StyledContainer columns={3} alignContent="center" justifyContent="center" margin="2rem 0 0 0">
            <Cell middle left={2}>
              <Button tag="button_save" type="button" fullWidth onClick={handleClose(resetForm)}>
                Cancel
              </Button>
            </Cell>
            <Cell middle left={3}>
              <Button tag="button_cancel" type="submit" fullWidth buttonModifiers={['buttonPrimary']}>
                Save
              </Button>
            </Cell>
          </StyledContainer>
        </form>
      )}
    </Formik>
  );
};

export default CreateUpdateClientAgentForm;
